import { DeleteOutlined, EditOutlined, NoteAltOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { ACTIONS } from 'constant';
import useTranslate from 'hooks/useTranslate';
import type { NoteInterface } from 'types';
import { formatCalendarDateTime, formatDateTime } from 'utils/dateUtils';

interface NotesListProps {
    notes: NoteInterface[];
    loading?: boolean;
    handleAction: (action: ACTIONS, note?: NoteInterface) => void;
    onDeleteSelected: (noteIds: number[]) => void;
    deleting?: boolean;
}

const actionIconButtonSx = {
    p: 0.5,
};

/** Shared column widths so header + body stay aligned. */
const COL = {
    select: 48,
    title: '22%',
    date: 150,
    actions: 96,
} as const;

const headCellSx = {
    py: 1.25,
    px: 1.5,
    fontWeight: 700,
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
    py: 1.5,
    px: 1.5,
    verticalAlign: 'middle' as const,
};

const selectCellSx = {
    width: COL.select,
    minWidth: COL.select,
    maxWidth: COL.select,
    py: 1.5,
    px: 0,
    verticalAlign: 'middle' as const,
    textAlign: 'center' as const,
};

const clampContentSx = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    lineHeight: 1.45,
    color: 'text.secondary',
};

function RowActions({
    note,
    handleAction,
    deleting,
}: {
    note: NoteInterface;
    handleAction: (action: ACTIONS, note?: NoteInterface) => void;
    deleting?: boolean;
}) {
    const { translate } = useTranslate();

    return (
        <Stack direction="row" spacing={0.25} sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title={translate('editNote')}>
                <IconButton
                    size="small"
                    aria-label={translate('editNote')}
                    onClick={() => handleAction(ACTIONS.EDIT, note)}
                    disabled={deleting}
                    sx={actionIconButtonSx}
                >
                    <EditOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
            <Tooltip title={translate('deleteNote')}>
                <IconButton
                    size="small"
                    color="error"
                    aria-label={translate('deleteNote')}
                    onClick={() => handleAction(ACTIONS.DELETE, note)}
                    disabled={deleting}
                    sx={actionIconButtonSx}
                >
                    <DeleteOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

interface SelectionBarProps {
    allSelected: boolean;
    someSelected: boolean;
    selectedCount: number;
    deleting?: boolean;
    disabled?: boolean;
    onToggleAll: (checked: boolean) => void;
    onDeleteSelected: () => void;
}

const SelectionBar: React.FC<SelectionBarProps> = ({
    allSelected,
    someSelected,
    selectedCount,
    deleting,
    disabled,
    onToggleAll,
    onDeleteSelected,
}) => {
    const { translate } = useTranslate();
    const hasSelection = selectedCount > 0;

    return (
        <Stack
            direction="row"
            spacing={1.5}
            sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.25,
                minHeight: 52,
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: hasSelection ? 'action.selected' : 'grey.50',
                transition: 'background-color 0.15s ease',
            }}
        >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={disabled}
                    onChange={(e) => onToggleAll(e.target.checked)}
                    sx={{ p: 0.5 }}
                    slotProps={{
                        input: { 'aria-label': translate('selectAllNotes') },
                    }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {hasSelection
                        ? translate('notesSelected').replace('{{count}}', String(selectedCount))
                        : translate('selectAllNotes')}
                </Typography>
            </Stack>

            <Button
                size="small"
                color="error"
                variant={hasSelection ? 'contained' : 'text'}
                startIcon={
                    deleting ? (
                        <CircularProgress size={14} color="inherit" />
                    ) : (
                        <DeleteOutlined fontSize="small" />
                    )
                }
                disabled={!hasSelection || deleting || disabled}
                onClick={onDeleteSelected}
            >
                {translate('deleteSelected')}
            </Button>
        </Stack>
    );
};

const NotesList: React.FC<NotesListProps> = ({
    notes,
    loading,
    handleAction,
    onDeleteSelected,
    deleting,
}) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    useEffect(() => {
        setSelectedIds((prev) => prev.filter((id) => notes.some((n) => n.id === id)));
    }, [notes]);

    const allSelected = notes.length > 0 && selectedIds.length === notes.length;
    const someSelected = selectedIds.length > 0 && !allSelected;

    const sortedNotes = useMemo(
        () =>
            [...notes].sort(
                (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            ),
        [notes]
    );

    const toggleAll = (checked: boolean) => {
        setSelectedIds(checked ? notes.map((n) => n.id) : []);
    };

    const toggleOne = (id: number, checked: boolean) => {
        setSelectedIds((prev) =>
            checked ? [...prev, id] : prev.filter((noteId) => noteId !== id)
        );
    };

    const handleBulkDelete = () => {
        if (!selectedIds.length) return;
        onDeleteSelected(selectedIds);
    };

    if (loading) {
        return <TableSkeleton tableColumns={5} tableRowCount={5} />;
    }

    if (!notes.length) {
        return (
            <Result
                icon={<NoteAltOutlined sx={{ fontSize: 32 }} />}
                title={translate('noNotesYet')}
                description={translate('noNotesYetDescription')}
            />
        );
    }

    if (isMobile) {
        return (
            <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                <SelectionBar
                    allSelected={allSelected}
                    someSelected={someSelected}
                    selectedCount={selectedIds.length}
                    deleting={deleting}
                    onToggleAll={toggleAll}
                    onDeleteSelected={handleBulkDelete}
                />
                <Stack
                    divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}
                    sx={{ maxHeight: 520, overflowY: 'auto' }}
                >
                    {sortedNotes.map((note) => {
                        const checked = selectedIds.includes(note.id);
                        return (
                            <Box
                                key={note.id}
                                sx={{
                                    px: 1.5,
                                    py: 1.5,
                                    bgcolor: checked ? 'action.selected' : 'transparent',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ alignItems: 'flex-start' }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={(e) => toggleOne(note.id, e.target.checked)}
                                        sx={{ p: 0.5, mt: 0.25 }}
                                        slotProps={{
                                            input: {
                                                'aria-label': note.title || translate('noteTitle'),
                                            },
                                        }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    lineHeight: 1.35,
                                                    textTransform: 'capitalize',
                                                    minWidth: 0,
                                                }}
                                                noWrap
                                            >
                                                {note.title || translate('noteTitle')}
                                            </Typography>
                                            <RowActions
                                                note={note}
                                                handleAction={handleAction}
                                                deleting={deleting}
                                            />
                                        </Stack>
                                        <Typography
                                            variant="body2"
                                            sx={{ ...clampContentSx, mb: 0.75 }}
                                        >
                                            {note.content || '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatCalendarDateTime(note.timestamp)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
            {/* Always-visible selection bar — no layout jump when selecting rows */}
            <SelectionBar
                allSelected={allSelected}
                someSelected={someSelected}
                selectedCount={selectedIds.length}
                deleting={deleting}
                onToggleAll={toggleAll}
                onDeleteSelected={handleBulkDelete}
            />

            <TableContainer
                sx={{
                    maxHeight: 520,
                    overflow: 'auto',
                    overscrollBehavior: 'contain',
                }}
            >
                <Table size="small" sx={{ minWidth: 680, tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            {/* Spacer column matches body checkbox width — select-all lives in the bar */}
                            <TableCell sx={{ ...selectCellSx, ...headCellSx, py: 1.25 }} />
                            <TableCell sx={{ ...headCellSx, width: COL.title }}>
                                {translate('title')}
                            </TableCell>
                            <TableCell sx={headCellSx}>{translate('noteContent')}</TableCell>
                            <TableCell sx={{ ...headCellSx, width: COL.date }}>
                                {translate('date')}
                            </TableCell>
                            <TableCell align="right" sx={{ ...headCellSx, width: COL.actions }}>
                                {translate('actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedNotes.map((note) => {
                            const checked = selectedIds.includes(note.id);
                            return (
                                <TableRow
                                    key={note.id}
                                    hover
                                    selected={checked}
                                    sx={{
                                        '&:last-child td': { borderBottom: 0 },
                                    }}
                                >
                                    <TableCell sx={selectCellSx}>
                                        <Checkbox
                                            size="small"
                                            checked={checked}
                                            onChange={(e) => toggleOne(note.id, e.target.checked)}
                                            sx={{ p: 0.5 }}
                                            slotProps={{
                                                input: {
                                                    'aria-label':
                                                        note.title || translate('noteTitle'),
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, width: COL.title }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                lineHeight: 1.35,
                                                textTransform: 'capitalize',
                                            }}
                                            noWrap
                                            title={note.title || undefined}
                                        >
                                            {note.title || translate('noteTitle')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Tooltip
                                            title={note.content || ''}
                                            placement="bottom-start"
                                            enterDelay={500}
                                            disableHoverListener={!note.content}
                                        >
                                            <Typography variant="body2" sx={clampContentSx}>
                                                {note.content || '—'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, width: COL.date }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: 'block',
                                                whiteSpace: 'nowrap',
                                                lineHeight: 1.4,
                                            }}
                                            title={formatDateTime(note.timestamp)}
                                        >
                                            {formatCalendarDateTime(note.timestamp)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ ...bodyCellSx, width: COL.actions }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <RowActions
                                                note={note}
                                                handleAction={handleAction}
                                                deleting={deleting}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default NotesList;
